import { ChatBubbleLeftIcon, DocumentIcon, PhotoIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { format, isSameDay } from "date-fns";
import { useLayoutEffect, useMemo, useRef } from "react";

import { Paragraph, Small, WYSIWYG } from "@fronterahealth/frontera-ui-components";

import { type ExpertChatMessage } from "@api/rest/types-and-hooks";

import { LOAD_OLDER_THRESHOLD_PX } from "./constants";

interface ExpertChatTranscriptProps {
  messages: ExpertChatMessage[];
  isLoading: boolean;
  hasOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
}

const authorName = (message: ExpertChatMessage) => {
  const name = `${message.author.first_name} ${message.author.last_name}`.trim();
  if (name) return name;
  return message.author.is_provider ? "Expert" : "Caregiver";
};

/** `4:45`-style offset into the video a comment was left at. */
const formatMediaTimeOffset = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safeSeconds / 60)}:${(safeSeconds % 60).toString().padStart(2, "0")}`;
};

/**
 * Subheader for a role=COMMENT echo bubble — a comment left on a video,
 * surfaced in the chat.
 *
 * Clarity's resolveEchoSubheader also has viewer-relative wordings ("You
 * commented on video", "Replied to your comment"). Those collapse here: a
 * Basecamp admin is never a participant, so only the neutral forms apply.
 */
const CommentSubheader: React.FC<{ message: ExpertChatMessage }> = ({ message }) => {
  const comment = message.media_comment;
  if (!comment) return null;
  const prefix = comment.is_reply ? "Reply on video" : "Comment on video";
  const anchor = comment.media_start_time !== null ? ` @ ${formatMediaTimeOffset(comment.media_start_time)}` : "";
  return (
    <span className="mb-1 inline-flex items-center gap-1">
      <ChatBubbleLeftIcon className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" />
      <Small colorType="secondary">{`${prefix}${anchor}`}</Small>
    </span>
  );
};

/** Label shown above a bubble that carries an attachment. */
const AttachmentMarker: React.FC<{ message: ExpertChatMessage }> = ({ message }) => {
  if (!message.file && !message.media) return null;
  const isVideo = !!message.media;
  const Icon = isVideo ? VideoCameraIcon : message.file?.is_image ? PhotoIcon : DocumentIcon;
  const label = isVideo ? "Video" : message.file?.is_image ? "Image" : "Attachment";
  return (
    <span className="mb-1 inline-flex items-center gap-1">
      <Icon className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" />
      <Small colorType="secondary">{label}</Small>
    </span>
  );
};

/** Renders the attachment in place — no separate media screen. */
const Attachment: React.FC<{ message: ExpertChatMessage }> = ({ message }) => {
  const { file, media } = message;

  if (media) {
    return media.thumbnail_url ? (
      <img
        src={media.thumbnail_url}
        alt="Video thumbnail"
        className="mt-2 max-h-64 rounded-md border border-border-subtle object-contain"
      />
    ) : (
      <span className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-default px-2 py-1">
        <VideoCameraIcon className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
        <Small colorType="secondary">
          {media.upload_status ? `Video (${media.upload_status.toLowerCase()})` : "Video"}
        </Small>
      </span>
    );
  }

  if (!file) return null;

  if (file.is_image && file.signed_url) {
    return (
      <img
        src={file.signed_url}
        alt={file.original_file_name || "Attachment"}
        className="mt-2 max-h-64 rounded-md border border-border-subtle object-contain"
      />
    );
  }

  // Non-image files stay a chip; a signed URL makes it openable.
  const chip = (
    <span className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-default px-2 py-1">
      <DocumentIcon className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
      <Small colorType="secondary">{file.original_file_name || "Attachment"}</Small>
    </span>
  );
  return file.signed_url ? (
    <a href={file.signed_url} target="_blank" rel="noreferrer" className="block w-fit">
      {chip}
    </a>
  ) : (
    chip
  );
};

export const ExpertChatTranscript: React.FC<ExpertChatTranscriptProps> = ({
  messages,
  isLoading,
  hasOlder,
  isLoadingOlder,
  onLoadOlder,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only the expert's most recent message carries a receipt, matching the
  // Clarity view: earlier ones are implied read by the later reply.
  const lastExpertMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].author.is_provider) return messages[i].id;
    }
    return null;
  }, [messages]);

  // Message the user was reading when a page of older messages was requested,
  // plus its offset inside the scroll container. Restoring against a real
  // element rather than a scrollHeight delta keeps the view stable even as the
  // Quill editors mount and change height after the prepend.
  const anchorRef = useRef<{ id: number; top: number } | null>(null);
  const didInitialPin = useRef(false);

  const requestOlder = () => {
    const node = scrollRef.current;
    if (!node || !hasOlder || isLoadingOlder || messages.length === 0) return;
    const firstId = messages[0].id;
    const firstEl = node.querySelector<HTMLElement>(`[data-mid="${firstId}"]`);
    anchorRef.current = firstEl ? { id: firstId, top: firstEl.offsetTop } : null;
    onLoadOlder();
  };

  const handleScroll = () => {
    const node = scrollRef.current;
    if (node && node.scrollTop <= LOAD_OLDER_THRESHOLD_PX) requestOlder();
  };

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node || messages.length === 0) return;

    // Open on the newest message; scrolling up walks back through history.
    if (!didInitialPin.current) {
      didInitialPin.current = true;
      const pin = () => {
        node.scrollTop = node.scrollHeight;
      };
      pin();
      // Re-pin next frame: the editors lay out asynchronously, so the first
      // measurement lands short of the bottom.
      const frame = requestAnimationFrame(pin);
      return () => cancelAnimationFrame(frame);
    }

    // Older messages were prepended — hold the anchor where the user left it.
    const restore = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const el = node.querySelector<HTMLElement>(`[data-mid="${anchor.id}"]`);
      if (!el) return;
      node.scrollTop += el.offsetTop - anchor.top;
      // Re-baseline so the next frame only corrects further growth.
      anchor.top = el.offsetTop;
    };
    restore();
    const frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [messages]);

  if (isLoading) {
    return (
      <div className="flex-1 px-5 py-4">
        <Small colorType="secondary">Loading messages…</Small>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-5 py-10">
        <Paragraph size="small" colorType="secondary">
          No messages in this chat yet.
        </Paragraph>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden rounded-md border border-border-subtle bg-bg-surface px-5 py-4"
    >
      {isLoadingOlder && (
        <Small colorType="secondary" className="mb-2 block text-center">
          Loading older messages…
        </Small>
      )}
      <ul className="flex list-none flex-col gap-3 p-0 m-0 min-w-0">
        {messages.map((message, index) => {
          const previous = index > 0 ? messages[index - 1] : null;
          const showDayDivider =
            !previous || !isSameDay(new Date(previous.created_date), new Date(message.created_date));
          const fromExpert = message.author.is_provider;

          return (
            <li key={message.id} data-mid={message.id} className="flex flex-col">
              {showDayDivider && (
                <div className="my-2 flex items-center gap-3">
                  <span className="h-px flex-1 bg-border-subtle" />
                  <Small colorType="secondary">{format(new Date(message.created_date), "MMM dd yyyy")}</Small>
                  <span className="h-px flex-1 bg-border-subtle" />
                </div>
              )}
              <div className={`flex ${fromExpert ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[85%] min-w-0">
                  <Small colorType="secondary" className="mb-0.5 block">
                    {authorName(message)} · {format(new Date(message.created_date), "h:mm a")}
                    {message.edited ? " · edited" : ""}
                  </Small>
                  <CommentSubheader message={message} />
                  <AttachmentMarker message={message} />
                  <div
                    className={`rounded-lg px-3 py-2 text-sm shadow-sm ${
                      fromExpert ? "bg-bg-default" : "bg-bg-surface border border-border-subtle"
                    }`}
                  >
                    {/* Message bodies are Quill HTML, so they render through
                        the WYSIWYG in display-only mode rather than as text. */}
                    <WYSIWYG
                      displayOnlyMode
                      editMode={false}
                      showTitle={false}
                      title=""
                      placeholder=""
                      existingContent={message.content}
                      isSaving={false}
                      onSave={async () => {}}
                    />
                    <Attachment message={message} />
                  </div>
                  {message.id === lastExpertMessageId && (
                    <Small colorType="secondary" className="mt-0.5 block text-right">
                      {message.read_at ? `Seen ${format(new Date(message.read_at), "h:mm a")}` : "Delivered"}
                    </Small>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
